import {
  Button,
  Container,
  CssBaseline,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material'
import VideoRecorder from 'components/VideoRecorder'
import React from 'react'
import * as Device from 'react-device-detect'
import images from 'utils/images'
import { DemonstrationActions, DemonstrationStoreStates } from '.'
import { DemonstrationAudio, DemonstrationList, ListTitle, TableContainer } from './styles'

type OwnProps = {}

type DemonstrationProps = OwnProps & DemonstrationStoreStates & DemonstrationActions

const Demonstration = (props: DemonstrationProps) => {
  const { movies, readingWords } = props
  const { items, loading } = readingWords.demonstration
  const { uploading, result } = movies.demonstration

  const [recording, setRecording] = React.useState(false)
  const [preUploading, setPreUploading] = React.useState(uploading)
  const [isUploaded, setIsUploaded] = React.useState(false)

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))

  const isBrowser = Device.isBrowser && !Device.isIOS13 // iPadOS 対策
  const isMobile = Device.isMobile || Device.isIOS13 // iPadOS 対策

  const cols = isMobile ? 1 : 5
  const listItems = Array(Math.ceil(items.length / cols))
    .fill(0)
    .map((_, i) => items.slice(cols * i, cols * (i + 1)))

  const contributeFile = (data?: File | Blob | null) => {
    if (!data) return
    props.upload(data)
  }

  React.useEffect(() => {
    // ワードの読み込み
    props.loadWords()
  }, [])

  React.useEffect(() => {
    if (preUploading === uploading) return

    const pre = preUploading

    setTimeout(() => {
      setIsUploaded(pre && !uploading)
    }, 200)

    setPreUploading(uploading)
  }, [uploading, preUploading])

  return (
    <Container>
      <CssBaseline />
      <Paper sx={{ p: 1, mb: 2 }}>
        <Grid
          container
          direction="column"
          sx={{
            alignItems: 'center',
          }}>
          <ListTitle align="left">はじめに</ListTitle>
          <Grid>
            <DemonstrationList>
              <li>本デモは日本語25単語を対象とした単語読唇技術のデモソフトウェアです。</li>
              <li>
                発話シーンのビデオファイルをアップロードしていただきます。ネットワークの通信速度によって、ビデオファイルをアップロードしてから結果が表示されるまで10秒以上かかることがあります。
              </li>
              <li>
                アップロードできるビデオのファイル容量は最大8MBです。これ以上の容量のファイルをアップロードすることはできません。
              </li>
              <li>ビデオファイルの再生時間が1秒未満あるいは10秒以上の場合はエラーメッセージが表示されます。</li>
              <li>顔が検出できない場合はエラーメッセージが表示されます。</li>
              <li>学習データの多くは若年者です。子供や高齢者の方は誤認識になりやすいです。ご了承ください。</li>
              <li>
                アップロードしていただいたビデオファイル、利用時間および認識結果（利用者データと定義します）は、本研究室が管理しているサーバに記録されます。
              </li>
              <li>利用者データは読唇技術の性能向上・改良の目的のために様々な形で利用させていただきます。</li>
              <li>
                本技術は、第11回バイオメトリクスと認識・認証シンポジウム（SBRA2021）で発表した内容に基づいていますが、予告なしに変更する場合があります。大幅な変更に関しては、本サイトで明記します。
              </li>
            </DemonstrationList>
          </Grid>
        </Grid>
        <Grid
          container
          direction="column"
          sx={{
            alignItems: 'center',
          }}>
          <ListTitle align="left">認識対象</ListTitle>
          {!loading && listItems.length === 0 && <Typography>現在認識できるワードはありません</Typography>}
          {listItems.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {Array(listItems[0].length)
                      .fill(0)
                      .map((_, i) => (
                        <TableCell key={i}></TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listItems.map((words, index) => (
                    <TableRow key={index} tabIndex={-1}>
                      {words.map((word) => (
                        <TableCell key={word.id}>{word.text}</TableCell>
                      ))}
                      {Array(5 - words.length)
                        .fill(0)
                        .map((_, index) => (
                          <TableCell key={index}></TableCell>
                        ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
        <Grid
          container
          direction="column"
          sx={{
            alignItems: 'center',
          }}>
          <ListTitle align="left">試す</ListTitle>
          {isBrowser && !recording && (
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: 'center',
                maxWidth: 600,
              }}>
              <Grid
                container
                sx={{
                  justifyContent: 'flex-end',
                }}
                size="grow">
                <Button
                  variant="outlined"
                  component="label"
                  color="primary"
                  disabled={uploading}
                  onClick={() => setRecording(true)}
                  sx={{ m: 2 }}>
                  動画像を撮影する
                </Button>
              </Grid>
              <Grid
                container
                sx={{
                  justifyContent: 'flex-start',
                }}
                size="grow">
                <Button variant="outlined" component="label" color="primary" disabled={uploading} sx={{ m: 2 }}>
                  ファイルから選択する
                  <input
                    type="file"
                    accept="video/*"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      contributeFile(e.target.files?.item(0))
                    }}
                  />
                </Button>
              </Grid>
            </Grid>
          )}
          {isBrowser && recording && (
            <VideoRecorder
              display={recording}
              onPost={(data) => {
                setRecording(false)
                contributeFile(data)
              }}
            />
          )}
          {isMobile && (
            <Grid>
              <Button variant="outlined" component="label" color="primary" disabled={uploading} sx={{ m: 2 }}>
                アップロードする
                <input
                  type="file"
                  accept="video/*"
                  style={{ display: 'none' }}
                  onChange={async (e) => contributeFile(e.target.files?.item(0))}
                />
              </Button>
            </Grid>
          )}
          {uploading && (
            <Grid>
              <Typography color="primary">アップロード中...</Typography>
            </Grid>
          )}
          {isUploaded && result && result.error && (
            <Grid>
              <Typography color="secondary">{result.error}</Typography>
            </Grid>
          )}
          {isUploaded && result && result.results && (
            <Grid>
              <ListTitle align="center">認識結果</ListTitle>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {!mdUp && <TableCell>ワード</TableCell>}
                      {!mdDown && (
                        <>
                          <TableCell>ワード</TableCell>
                          <TableCell>精度</TableCell>
                          <TableCell>音声</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.results.map((item, index) => (
                      <TableRow key={index} tabIndex={-1}>
                        {!mdUp && (
                          <TableCell>
                            {item.word} ({item.accuracy * 100} %)
                            <DemonstrationAudio
                              controls
                              controlsList="nodownload"
                              src={item.sound}
                              style={{ marginTop: 10 }}
                            />
                          </TableCell>
                        )}
                        {!mdDown && (
                          <>
                            <TableCell>{item.word}</TableCell>
                            <TableCell>{item.accuracy * 100} %</TableCell>
                            <TableCell>
                              <DemonstrationAudio controls controlsList="nodownload" src={item.sound} />
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid
                container
                sx={{
                  alignItems: 'center',
                  alignContent: 'center',
                  justifyContent: 'center',
                }}>
                <img src={result.image} alt="result" style={{ maxHeight: 300, objectFit: 'contain' }} />
              </Grid>
            </Grid>
          )}
          <ListTitle align="left">使い方</ListTitle>
          <Grid
            container
            direction="column"
            sx={{
              alignItems: 'center',
              mt: 2,
            }}>
            <Typography>以下の撮影例を参考にしてください。</Typography>
            <Grid>
              <img src={images.contributionSample} alt="sample" style={{ marginLeft: '5%', maxWidth: '90%' }} />
            </Grid>
          </Grid>
          <Grid>
            <DemonstrationList>
              <li>撮影中はカメラを動かさないでください。</li>
              <li>録画開始になってから口を動かし始めてください。</li>
              <li>発話開始の前後は口を閉じてください。</li>
              <li>顔は画面中央に位置するようにして発話してください。</li>
              <li>顔は画面中央を向いて発話してください。</li>
              <li>上の言葉を自然な発話速度・口の動きで発話してください。</li>
              <li>
                早すぎる発話／ゆっくりすぎる発話、口を大きく開けすぎる発話／口の開きが小さい発話は誤認識の原因になります。
              </li>
            </DemonstrationList>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  )
}

export default Demonstration
